import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, CreditCard, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { publicApi, api, errMsg } from "@/lib/api";
import { inr } from "@/lib/format";

function Brand({ b }) {
  return (
    <div className="flex items-center gap-3">
      {b?.logo_url && <img src={b.logo_url} alt="logo" className="h-12 w-12 object-contain" data-testid="join-logo" />}
      <div>
        <div className="font-display text-xl font-extrabold text-[#0F382C]">{b?.business_name || "Nex Fresh"}</div>
        <div className="text-xs text-muted-foreground">{b?.tagline}</div>
      </div>
    </div>
  );
}

function JoinForm({ onDone }) {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [f, setF] = useState({ name: "", phone: "", address: "", area: "", notes: "", ref, birthday: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await publicApi.post("/join", f); onDone(data.token); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const field = (k, label, props = {}) => (
    <div><label className="text-sm font-semibold">{label}</label>
      <Input className="mt-1 h-12 rounded-xl bg-white" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`join-${k}-input`} {...props} /></div>
  );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="join-form">
      {ref && <div className="rounded-xl border border-[#F2B880] bg-[#FFF7EC] p-3 text-sm text-[#0F382C]" data-testid="join-referral-banner">A friend invited you to Nex Fresh. Welcome!</div>}
      {field("name", "Your name", { required: true })}
      {field("phone", "WhatsApp number", { required: true, inputMode: "numeric", placeholder: "10-digit mobile" })}
      <div><label className="text-sm font-semibold">Delivery address</label>
        <Textarea className="mt-1 rounded-xl bg-white" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="join-address-input" /></div>
      {field("area", "Area / locality", { placeholder: "e.g. HSR Layout" })}
      {field("birthday", "Birthday (optional, for a birthday treat)", { placeholder: "DD-MM, e.g. 15-08", maxLength: 5 })}
      {field("notes", "Anything we should know? (optional)")}
      <Button disabled={busy} className="h-14 w-full rounded-2xl bg-[#0F382C] text-base hover:bg-[#134E3A]" data-testid="join-submit-btn">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Continue to membership payment"}
      </Button>
    </form>
  );
}

function PayStep({ reg, reload }) {
  const [busy, setBusy] = useState(false);
  const payOnline = async () => {
    setBusy(true);
    try { const { data } = await publicApi.post(`/join/${reg.token}/pay-link`); window.location.href = data.short_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (reg.status === "active")
    return (
      <div className="rounded-3xl bg-[#0F382C] p-6 text-white" data-testid="join-success">
        <CheckCircle2 className="h-10 w-10 text-emerald-300" />
        <div className="font-display text-2xl font-bold mt-3">Welcome to Nex Fresh, {reg.name.split(" ")[0]}!</div>
        <p className="mt-2 text-emerald-100/90 text-sm">Your membership is active. Order fresh batter for tomorrow anytime from your personal page.</p>
        {reg.portal_token && <a href={`/c/${reg.portal_token}/order`} className="mt-5 block rounded-2xl bg-[#FBF9F4] py-4 text-center font-bold text-[#0F382C]" data-testid="join-portal-link">Order tomorrow's batter</a>}
      </div>
    );
  if (reg.status === "rejected") return <div className="rounded-2xl bg-rose-50 p-5 text-rose-700" data-testid="join-rejected">This sign-up was not approved. Please contact us.</div>;
  return (
    <div data-testid="join-pay-step">
      <div className="rounded-3xl bg-[#0F382C] p-6 text-white">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-200">One-time membership</div>
        <div className="font-mono text-4xl font-semibold mt-2" data-testid="join-fee">{inr(reg.fee)}</div>
        <p className="mt-2 text-sm text-emerald-100/90">Hi {reg.name.split(" ")[0]}, pay the membership to start daily doorstep delivery.</p>
        {reg.razorpay_enabled && (
          <button onClick={payOnline} disabled={busy} className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#FBF9F4] py-4 font-bold text-[#0F382C] disabled:opacity-60" data-testid="join-razorpay-btn">
            <CreditCard className="h-5 w-5" />{busy ? "Opening secure payment..." : "Pay online (UPI / Card / Netbanking)"}
          </button>
        )}
        {reg.upi_link && (
          <a href={reg.upi_link} className="mt-3 flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-purple-700 to-indigo-700 py-4 font-bold" data-testid="join-upi-btn"><Smartphone className="h-5 w-5" />Pay via UPI app</a>
        )}
        {!reg.razorpay_enabled && !reg.upi_link && <p className="mt-4 text-sm text-emerald-100/80" data-testid="join-offline-note">We'll call you on WhatsApp to collect the membership with your first delivery.</p>}
      </div>
      <p className="mt-4 text-sm text-muted-foreground">Paid by UPI? We'll confirm and activate your membership shortly.</p>
      <Button variant="outline" className="mt-3 w-full" onClick={reload} data-testid="join-refresh-btn">I've paid, check status</Button>
    </div>
  );
}

export default function Join() {
  const { token } = useParams();
  const nav = useNavigate();
  const [reg, setReg] = useState(null);
  const [brand, setBrand] = useState(null);
  useEffect(() => { api.get("/brand").then((r) => setBrand(r.data)).catch(() => {}); }, []);
  const load = useCallback(() => {
    if (token) publicApi.get(`/join/${token}`).then((r) => setReg(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [token]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="min-h-screen bg-[#FBF9F4]" data-testid="join-page">
      <div className="mx-auto max-w-md px-5 py-8">
        <Brand b={reg?.business || brand} />
        <h1 className="font-display text-3xl font-bold text-[#0F382C] mt-8">{token ? "Your membership" : "Become a Nex Fresh member"}</h1>
        {!token && <p className="text-muted-foreground mt-2 mb-6">Fresh, preservative-free batter delivered to your door every morning. Pay weekly, skip any day, pause for holidays.</p>}
        <div className="mt-6">
          {!token ? <JoinForm onDone={(t) => nav(`/join/${t}`)} /> : reg ? <PayStep reg={reg} reload={load} /> : <Loader2 className="mx-auto h-6 w-6 animate-spin" />}
        </div>
      </div>
    </div>
  );
}
